import React, { useEffect, useState } from 'react';
import { X, Share2, Trophy } from 'lucide-react';
import type { Achievement } from '../types/learning';
import { ACHIEVEMENT_POINTS } from '../data/achievements';

interface AchievementNotificationProps {
  achievement: Achievement;
  onClose: () => void;
}

export const AchievementNotification: React.FC<AchievementNotificationProps> = ({
  achievement,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Entrance animation
    setTimeout(() => setIsVisible(true), 50);

    // Auto-dismiss after 8 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const getTierColor = (tier: Achievement['tier']) => {
    switch (tier) {
      case 'bronze': return 'from-amber-600 to-amber-700';
      case 'silver': return 'from-gray-400 to-gray-500';
      case 'gold': return 'from-amber-400 to-amber-500';
      case 'platinum': return 'from-purple-400 to-purple-500';
    }
  };

  const getTierGlow = (tier: Achievement['tier']) => {
    switch (tier) {
      case 'bronze': return 'shadow-amber-500/50';
      case 'silver': return 'shadow-gray-400/50';
      case 'gold': return 'shadow-amber-400/50';
      case 'platinum': return 'shadow-purple-400/50';
    }
  };

  const points = ACHIEVEMENT_POINTS[achievement.tier];

  return (
    <div
      className={`
        fixed top-20 right-6 z-50 w-96
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className={`
        bg-white rounded-xl shadow-2xl border-2 border-primary-200
        overflow-hidden ${getTierGlow(achievement.tier)}
      `}>
        {/* Tier Banner */}
        <div className={`
          bg-gradient-to-r ${getTierColor(achievement.tier)}
          px-4 py-2 flex items-center justify-between
        `}>
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-white" />
            <span className="text-white font-bold text-sm uppercase tracking-wide">
              {achievement.tier} Achievement
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>
        </div>

        {/* Achievement Content */}
        <div className="p-6">
          {/* Celebration Animation */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="confetti-animation">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="confetti"
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    backgroundColor: ['#f59187', '#f5b969', '#627d98', '#8cb299'][Math.floor(Math.random() * 4)],
                  }}
                />
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Icon and Title */}
            <div className="flex items-start gap-4 mb-4">
              <div className="text-6xl animate-bounce-slow">
                {achievement.icon}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-charcoal-900 mb-1">
                  {achievement.name}
                </h3>
                <p className="text-sm text-charcoal-600">
                  {achievement.description}
                </p>
              </div>
            </div>

            {/* Points Earned */}
            <div className="bg-gradient-to-r from-primary-50 to-sage-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-charcoal-700">
                  Achievement Points
                </span>
                <span className="text-2xl font-bold text-primary-600">
                  +{points}
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-charcoal-600">Progress</span>
                <span className="text-xs font-semibold text-charcoal-900">
                  {achievement.progress}/{achievement.maxProgress}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${getTierColor(achievement.tier)} transition-all duration-1000`}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  // Share achievement (could integrate with social or copy to clipboard)
                  const text = `🎉 I just unlocked "${achievement.name}" in Peak Priorities Base Camp! ${achievement.icon}`;
                  navigator.clipboard.writeText(text);
                  // Could show toast: "Copied to clipboard!"
                }}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-charcoal-100 hover:bg-charcoal-200 text-charcoal-700 text-sm font-medium rounded-lg transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(720deg);
            opacity: 0;
          }
        }

        .confetti {
          position: absolute;
          top: -10px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: confetti-fall 3s ease-in forwards;
        }
      `}</style>
    </div>
  );
};

// Achievement Notification Manager Component
export const AchievementNotificationManager: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  useEffect(() => {
    const handleAchievementUnlock = (event: CustomEvent<Achievement>) => {
      setAchievements(prev => [...prev, event.detail]);
    };

    window.addEventListener('achievement-unlocked', handleAchievementUnlock as EventListener);

    return () => {
      window.removeEventListener('achievement-unlocked', handleAchievementUnlock as EventListener);
    };
  }, []);

  const handleClose = (index: number) => {
    setAchievements(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {achievements.map((achievement, index) => (
        <div
          key={`${achievement.id}-${index}`}
          style={{ top: `${20 + index * 10}px` }}
        >
          <AchievementNotification
            achievement={achievement}
            onClose={() => handleClose(index)}
          />
        </div>
      ))}
    </>
  );
};

